import { useMemo, useRef, useState } from 'react';

import type { SkuDTO } from '@invenco/products-interface';

import { Button } from '@/components/core/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/core/combobox';
import {
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/core/dialog';
import { useDialogContext } from '@/components/dialog-with-context';
import { useGateways } from '@/gateways/GatewayProvider';
import { SkuBadge } from '@/pages/seller/components/sku-badge';
import type { Result } from '@/shared/helpers/Result';
import { useAutoCompleteQuery } from '@/shared/hooks/queries';

import type { OrderLineRow } from './order-lines-card-columns';

type AssignSkuDialogContentProps = {
  line: OrderLineRow;
  onAssignSku: (line: OrderLineRow, sku: SkuDTO) => Promise<void | Result<unknown>>;
};

type SkuOption = {
  value: string;
  label: string;
  description: string | null;
  sku: SkuDTO;
};

function SkuOptionContent({ option }: { option: SkuOption }) {
  return (
    <div className="flex max-w-full min-w-0 items-center gap-2 overflow-hidden">
      <SkuBadge name={option.label} type={option.sku.type} />
      {option.description ? <span className="truncate text-sm">{option.description}</span> : null}
    </div>
  );
}

export function AssignSkuDialogContent({ line, onAssignSku }: AssignSkuDialogContentProps) {
  const { productsGateway } = useGateways();
  const { setIsOpen } = useDialogContext();
  const [selectedSku, setSelectedSku] = useState<SkuDTO | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  // TODO: shouldn't be necessary once using Base UI for dialogs
  const portalContainerRef = useRef<HTMLDivElement>(null);

  const {
    options: matchingSkus,
    onSearch,
    isLoading,
  } = useAutoCompleteQuery({
    parentKey: 'order-line-assign-skus',
    queryParams: { archived: false },
    query: (input, { signal }) => productsGateway.getSkus(input, { signal }),
    enabled: true,
  });

  const options = useMemo<SkuOption[]>(
    () =>
      matchingSkus.map((sku) => ({
        value: sku.id,
        label: sku.name,
        description: sku.description,
        sku,
      })),
    [matchingSkus],
  );

  async function handleAssign() {
    if (!selectedSku) return;

    setIsAssigning(true);
    try {
      const result = await onAssignSku(line, selectedSku);
      if (result?.isFailure) return;
      setIsOpen(false);
    } finally {
      setIsAssigning(false);
    }
  }

  return (
    <DialogContent className="sm:max-w-lg">
      <form
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          void handleAssign();
        }}
      >
        <DialogHeader>
          <DialogTitle>Assign SKU</DialogTitle>
          <DialogDescription>
            {line.skuName} was not recognised as a valid SKU. Please search for an existing SKU to
            replace it.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Combobox
            filteredItems={options}
            items={options}
            itemToStringLabel={(value) =>
              options.find((option) => option.value === value)?.description ?? ''
            }
            onInputValueChange={(value, eventDetails) => {
              const nextInputValue = typeof value === 'string' ? value : '';

              if (eventDetails.reason === 'item-press') {
                return;
              }

              onSearch(nextInputValue);
              if (
                eventDetails.reason === 'input-change' ||
                eventDetails.reason === 'input-clear' ||
                eventDetails.reason === 'clear-press'
              ) {
                setSelectedSku(null);
              }
            }}
            onValueChange={(value) => {
              const selectedValue = typeof value === 'string' ? value : undefined;
              if (!selectedValue) return;
              const selectedOption = options.find((option) => option.value === selectedValue);
              if (!selectedOption) return;
              setSelectedSku(selectedOption.sku);
            }}
          >
            <ComboboxInput
              placeholder={selectedSku ? '' : 'Search by SKU or description'}
              aria-label="SKU"
              showClear
            >
              {selectedSku ? (
                <span
                  className="order-first ml-2.5 max-w-[45%] min-w-0 shrink justify-start truncate"
                  title={selectedSku.name}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.currentTarget.parentElement?.querySelector('input')?.focus();
                  }}
                >
                  <SkuBadge name={selectedSku.name} type={selectedSku.type} />
                </span>
              ) : null}
            </ComboboxInput>
            <ComboboxContent portalContainer={portalContainerRef}>
              <ComboboxEmpty>{isLoading ? 'Loading SKUs...' : 'No matching SKUs'}</ComboboxEmpty>
              <ComboboxList>
                {(option: SkuOption) => (
                  <ComboboxItem key={option.value} value={option.value}>
                    <SkuOptionContent option={option} />
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </DialogBody>
        <DialogFooter>
          <div ref={portalContainerRef} />
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={!selectedSku} loading={isAssigning}>
            Assign
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
